import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { t } from "@/i18n-js/instance";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { useToast } from "@circle-react-shared/uikit/ToastV2";
import { INVALID_DROP_I18N } from "./constants";
import { TreeOperations } from "./services/TreeOperations";
import { ValidationOperations } from "./services/ValidationOperations";
import type { BlockMetadata, TreeItem } from "./types";

export const useSortableTree = () => {
  const { error } = useToast();
  const {
    root,
    dragAndDropType,
    currentPageId,
    sortBlocks,
    getBlock,
    getBlockMeta,
  } = useBuilderStore(state => ({
    root: state.getBlocks(),
    dragAndDropType: state.dragAndDropType,
    currentPageId: state.currentPageId,
    sortBlocks: state.sortBlocks,
    getBlock: state.getBlock,
    getBlockMeta: state.getBlockMeta,
  }));

  const [items, setItems] = useState<TreeItem[]>([]);
  const collapsedStateRef = useRef(new Map<string, boolean>());
  const previousRootChildrenRef = useRef<BaseBlock[] | null>(null);
  const validationCache = useRef(new Map<string, boolean>());

  const rootChildren = useMemo(() => {
    const children = root?.props?.children?.value;
    return Array.isArray(children) ? (children as BaseBlock[]) : [];
  }, [root?.props?.children?.value]);

  const sortBlocksImmediate = useCallback(
    (newItems: TreeItem[]) => {
      const reorderedBlocks = TreeOperations.convertTreeToBlocks(
        newItems,
        rootChildren,
      );
      sortBlocks(reorderedBlocks);
    },
    [sortBlocks, rootChildren],
  );

  const getBlockMetadata = useCallback(
    (blockId: string): BlockMetadata | null => {
      const blockData = getBlock(blockId);
      if (!blockData?.block) return null;

      const metaProps = blockData.meta?.props as
        | Record<string, unknown>
        | undefined;
      const { accepts = [], rejects = [] } = metaProps
        ? ValidationOperations.extractAcceptsRejects(metaProps)
        : {};

      return {
        category: blockData.meta?.category || [],
        accepts,
        rejects,
        metaProps,
      };
    },
    [getBlock],
  );

  const getPropertyNodeMetadata = useCallback(
    (parentId: string, propName: string) => {
      const parentMetadata = getBlockMetadata(parentId);
      const propDef = parentMetadata?.metaProps?.[propName];

      if (!propDef || propDef.type !== "children") return null;

      return {
        category: [],
        accepts: propDef.accepts || [],
        rejects: propDef.rejects || [],
        metaProps: undefined,
      };
    },
    [getBlockMetadata],
  );

  const canDropBlock = useCallback(
    (draggedItem: TreeItem, targetParentItem: TreeItem | null): boolean => {
      const cacheKey = `${draggedItem.id}:${targetParentItem?.id || "root"}`;
      const isCached = validationCache.current.get(cacheKey);
      if (isCached !== undefined) return isCached;

      if (TreeOperations.isPropertyNode(draggedItem)) {
        validationCache.current.set(cacheKey, false);
        return false;
      }

      const draggedBlockId = TreeOperations.getActualBlockId(draggedItem.id);
      const draggedMetadata = getBlockMetadata(draggedBlockId);
      if (!draggedMetadata) {
        validationCache.current.set(cacheKey, false);
        return false;
      }

      const canDrop = !targetParentItem
        ? ValidationOperations.canDropAtRoot(draggedMetadata, root?.meta)
        : TreeOperations.isPropertyNode(targetParentItem)
          ? ValidationOperations.canDropInPropertyNode(
              draggedMetadata,
              targetParentItem,
              getPropertyNodeMetadata,
            )
          : ValidationOperations.canDropInRegularBlock(
              draggedMetadata,
              targetParentItem,
              getBlockMetadata,
            );

      validationCache.current.set(cacheKey, canDrop);

      if (!canDrop) error(t(INVALID_DROP_I18N));
      return canDrop;
    },
    [getBlockMetadata, getPropertyNodeMetadata, root?.meta, error],
  );

  const validateTreeStructure = useCallback(
    (newItems: TreeItem[], originalItems: TreeItem[]): boolean => {
      if (newItems.length !== originalItems.length) {
        return newItems.every(item =>
          ValidationOperations.validateItemAndChildren(
            item,
            null,
            originalItems,
            newItems,
            canDropBlock,
          ),
        );
      }

      return newItems.every(
        (item, i) =>
          item === originalItems[i] ||
          ValidationOperations.validateItemAndChildren(
            item,
            null,
            originalItems,
            newItems,
            canDropBlock,
          ),
      );
    },
    [canDropBlock],
  );

  useEffect(() => {
    const isSameReference = previousRootChildrenRef.current === rootChildren;
    if (isSameReference) return;

    const isSameContent = TreeOperations.shallowArrayEqual(
      previousRootChildrenRef.current,
      rootChildren,
    );
    if (isSameContent) return;

    if (items.length > 0) {
      TreeOperations.updateCollapsedStateMap(items, collapsedStateRef.current);
    }

    const newTree = TreeOperations.convertBlocksToTree(
      rootChildren,
      getBlockMeta,
    );

    setItems(
      TreeOperations.applyCollapsedStateToTree(
        newTree,
        collapsedStateRef.current,
        false,
      ),
    );

    previousRootChildrenRef.current = rootChildren;
    validationCache.current.clear();
  }, [rootChildren, items.length, getBlockMeta]);

  const handleItemsChange = useCallback(
    (newItems: TreeItem[]) => {
      if (dragAndDropType === "disabled") return;

      if (items === newItems) {
        return;
      }
      if (TreeOperations.areTreesEqual(items, newItems)) {
        const currentCollapsedState = new Map<string, boolean>();
        TreeOperations.updateCollapsedStateMap(newItems, currentCollapsedState);
        const processedItems = TreeOperations.applyCollapsedStateToTree(
          newItems,
          currentCollapsedState,
          false,
        );
        setItems(processedItems);
        return;
      }

      if (
        ValidationOperations.checkForCircularReferences(newItems, items) ||
        !validateTreeStructure(newItems, items)
      )
        return;

      const currentCollapsedState = new Map<string, boolean>();
      TreeOperations.updateCollapsedStateMap(newItems, currentCollapsedState);

      const processedItems = TreeOperations.applyCollapsedStateToTree(
        newItems,
        currentCollapsedState,
        false,
      );

      setItems(processedItems);
      sortBlocksImmediate(processedItems);
    },
    [items, validateTreeStructure, dragAndDropType, sortBlocksImmediate],
  );

  useEffect(() => {
    validationCache.current.clear();
  }, [currentPageId]);

  return { items, handleItemsChange };
};
